import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Export the api instance for use in AuthContext
export { api };

// Request interceptor for logging
api.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error('API Response Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Workspace API functions
export const workspaceAPI = {
  // Get all workspaces
  getWorkspaces: async () => {
    try {
      const response = await api.get('/workspaces');
      return response.data;
    } catch (error) {
      console.error('Error fetching workspaces:', error);
      throw error;
    }
  },

  // Create new workspace
  createWorkspace: async (workspaceData) => {
    try {
      const response = await api.post('/workspaces', workspaceData);
      return response.data;
    } catch (error) {
      console.error('Error creating workspace:', error);
      throw error;
    }
  },

  // Get specific workspace
  getWorkspace: async (workspaceId) => {
    try {
      const response = await api.get(`/workspaces/${workspaceId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching workspace:', error);
      throw error;
    }
  },

  // Update workspace
  updateWorkspace: async (workspaceId, updateData) => {
    try {
      const response = await api.put(`/workspaces/${workspaceId}`, updateData);
      return response.data;
    } catch (error) {
      console.error('Error updating workspace:', error);
      throw error;
    }
  },

  // Delete workspace
  deleteWorkspace: async (workspaceId) => {
    try {
      const response = await api.delete(`/workspaces/${workspaceId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting workspace:', error);
      throw error;
    }
  },

  // Get boards in workspace
  getWorkspaceBoards: async (workspaceId) => {
    try {
      const response = await api.get(`/workspaces/${workspaceId}/boards`);
      return response.data;
    } catch (error) {
      console.error('Error fetching workspace boards:', error);
      throw error;
    }
  }
};

// Board API functions
export const boardAPI = {
  // Get all boards (optionally filtered by workspace)
  getBoards: async (workspaceId = null) => {
    try {
      const url = workspaceId ? `/boards?workspace_id=${workspaceId}` : '/boards';
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching boards:', error);
      throw error;
    }
  },

  // Create new board
  createBoard: async (boardData) => {
    try {
      const response = await api.post('/boards', boardData);
      return response.data;
    } catch (error) {
      console.error('Error creating board:', error);
      throw error;
    }
  },

  // Get specific board with lists and cards
  getBoard: async (boardId) => {
    try {
      const response = await api.get(`/boards/${boardId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching board:', error);
      throw error;
    }
  },

  // Update board
  updateBoard: async (boardId, updateData) => {
    try {
      const response = await api.put(`/boards/${boardId}`, updateData);
      return response.data;
    } catch (error) {
      console.error('Error updating board:', error);
      throw error;
    }
  },

  // Delete board
  deleteBoard: async (boardId) => {
    try {
      const response = await api.delete(`/boards/${boardId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting board:', error);
      throw error;
    }
  },

  // Reorder board items (for drag and drop)
  reorderBoard: async (boardId, boardData) => {
    try {
      const response = await api.put(`/boards/${boardId}/reorder`, boardData);
      return response.data;
    } catch (error) {
      console.error('Error reordering board:', error);
      throw error;
    }
  },

  // Delete board
  deleteBoard: async (boardId) => {
    try {
      const response = await api.delete(`/boards/${boardId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting board:', error);
      throw error;
    }
  }
};

// List API functions
export const listAPI = {
  // Create new list
  createList: async (boardId, listData) => {
    try {
      const response = await api.post(`/boards/${boardId}/lists`, listData);
      return response.data;
    } catch (error) {
      console.error('Error creating list:', error);
      throw error;
    }
  },

  // Update list
  updateList: async (listId, updateData) => {
    try {
      const response = await api.put(`/lists/${listId}`, updateData);
      return response.data;
    } catch (error) {
      console.error('Error updating list:', error);
      throw error;
    }
  },

  // Delete list
  deleteList: async (listId) => {
    try {
      const response = await api.delete(`/lists/${listId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting list:', error);
      throw error;
    }
  }
};

// Card API functions
export const cardAPI = {
  // Create new card
  createCard: async (listId, cardData) => {
    try {
      const response = await api.post(`/lists/${listId}/cards`, cardData);
      return response.data;
    } catch (error) {
      console.error('Error creating card:', error);
      throw error;
    }
  },

  // Update card
  updateCard: async (cardId, updateData) => {
    try {
      const response = await api.put(`/cards/${cardId}`, updateData);
      return response.data;
    } catch (error) {
      console.error('Error updating card:', error);
      throw error;
    }
  },

  // Move card to different list/position
  moveCard: async (cardId, moveData) => {
    try {
      const response = await api.put(`/cards/${cardId}/move`, moveData);
      return response.data;
    } catch (error) {
      console.error('Error moving card:', error);
      throw error;
    }
  },

  // Delete card
  deleteCard: async (cardId) => {
    try {
      const response = await api.delete(`/cards/${cardId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting card:', error);
      throw error;
    }
  }
};

// Error handling utility
export const handleAPIError = (error) => {
  if (error.response) {
    // Server responded with error status
    const status = error.response.status;
    const message = error.response.data?.detail || error.response.data?.message || 'Server error';
    
    switch (status) {
      case 404:
        return { type: 'NOT_FOUND', message: 'Resource not found' };
      case 400:
        return { type: 'VALIDATION_ERROR', message };
      case 500:
        return { type: 'SERVER_ERROR', message: 'Internal server error' };
      default:
        return { type: 'API_ERROR', message };
    }
  } else if (error.request) {
    // Network error or no response
    return { type: 'NETWORK_ERROR', message: 'Unable to connect to server' };
  } else {
    // Other error
    return { type: 'UNKNOWN_ERROR', message: error.message };
  }
};

// Add new functions to the main api object
api.auth = {
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },
  
  register: async (name, email, password) => {
    const response = await api.post('/auth/register', { name, email, password });
    return response.data;
  },
  
  me: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  requestPasswordReset: async (email) => {
    const response = await api.post('/auth/password-reset-request', { email });
    return response.data;
  },

  verifyResetToken: async (token) => {
    const response = await api.post('/auth/verify-reset-token', { token });
    return response.data;
  },

  resetPassword: async (token, newPassword) => {
    const response = await api.post('/auth/reset-password', { 
      token, 
      new_password: newPassword 
    });
    return response.data;
  }
};

// Workspace member management
api.workspaceMembers = {
  invite: async (workspaceId, email, role = 'member') => {
    const response = await api.post(`/workspaces/${workspaceId}/invite`, { email, role });
    return response.data;
  },
  
  getMembers: async (workspaceId) => {
    const response = await api.get(`/workspaces/${workspaceId}/members`);
    return response.data;
  },
  
  updateRole: async (workspaceId, userId, role) => {
    const response = await api.put(`/workspaces/${workspaceId}/members/${userId}`, { role });
    return response.data;
  },
  
  removeMember: async (workspaceId, userId) => {
    const response = await api.delete(`/workspaces/${workspaceId}/members/${userId}`);
    return response.data;
  }
};

// Admin API functions
api.admin = {
  getUsers: async () => {
    const response = await api.get('/admin/users');
    return response.data;
  },
  
  getUser: async (userId) => {
    const response = await api.get(`/admin/users/${userId}`);
    return response.data;
  },
  
  updateUser: async (userId, updateData) => {
    const response = await api.put(`/admin/users/${userId}`, updateData);
    return response.data;
  },
  
  getStats: async () => {
    const response = await api.get('/admin/stats');
    return response.data;
  }
};

export default api;